/**
 * 静的に配った画面のヘッダーを、ログインの状態に合わせる（docs/04 §5.2.1）。
 *
 * <b>静的な画面は誰が開くか分からずに描いてある</b>ので、ヘッダーは両方の状態を持つ
 * （<c>SiteApp/layout/site-header.tsx</c>）。開いてから <c>/api/session</c> を聞き、片方を出す。
 * <b>ログイン前の方が最初から出ている</b>——読めなくても入口は残る。
 *
 * <b>画面を移らずにログインしたときも直す。</b>会員の画面（React）はログインの後に
 * ページを読み込み直さないので、<c>portal:session</c> を投げてもらい、聞き直す。
 * 印 <c>&lt;html data-static-site&gt;</c> の無い画面では何もしない。
 *
 * <b>前に読んだ状態は控えておき、次のページでは描く前に当てる</b>（<c>auth-hint.ts</c>。管理センターの入口を遅れて出さない）。
 * <b>最後に決めるのはここで読んだ結果</b>——控えと違えば（ログアウトした・権限を外された・別の人）直し、控えも書き直す。
 * 控えは見た目のためだけで、開けるかどうかはサーバが毎回判定する（今までと同じ）。
 *
 * <b>ログアウトは、切れたことを確かめてから画面を移す。</b>送れなかった・断られた・送った後もログインのままなら、
 * 知らせてヘッダーをログイン中に戻す（黙ってトップへ移ると、ログアウトしたのに戻されたように見える。#218）。
 */

import { clearAuthHint, dropAuthHint, writeAuthHint } from "./auth-hint.js";

/** 状態が変わったときに投げる合図。 */
export const sessionChanged = "portal:session";

/** このタブでログインしていた印（会員の画面の <c>MemberApp/app/session-expiry.ts</c> と同じ名前）。 */
export const signedInMark = "portal.signedIn";

interface Session {
    readonly signedIn: boolean;
    readonly email: string | null;
    readonly token: string;
    readonly canOpenAdmin?: boolean;
}

/** 画面の外へ出る。テストでは差し替える（jsdom は移動できない）。 */
export type Leave = (location: string) => void;

const defaultLeave: Leave = (location) => {
    window.location.assign(location);
};

/** 利用者に知らせる。テストでは差し替える（jsdom は alert を出せない）。 */
export type Tell = (message: string) => void;

const defaultTell: Tell = (message) => {
    globalThis.alert(message);
};

/** ログアウトが効かなかったときの知らせ。 */
export const signOutFailedMessage = "ログアウトできませんでした。時間をおいて、もう一度お試しください。";

const jsonHeaders = { Accept: "application/json" } as const;

const readSession = async (): Promise<Session | undefined> => {
    try {
        const response = await fetch("/api/session", { headers: jsonHeaders });
        if (response.status === 401) {
            // ログインが切れた。控えを残すと、次のページでもログイン中に見える
            clearAuthHint();
        }
        return response.ok ? await response.json() as Session : undefined;
    } catch {
        // 読めなければ何もしない（ログイン前の入口が出たまま）
        return undefined;
    }
};

const show = (root: Document, session: Session): void => {
    const signedIn = root.querySelector<HTMLElement>("[data-auth=signed-in]");
    const signedOut = root.querySelector<HTMLElement>("[data-auth=signed-out]");
    if (signedIn === null || signedOut === null) {
        return;
    }

    signedIn.hidden = !session.signedIn;
    signedOut.hidden = session.signedIn;
    // このタブでログインしていた印。後で 401 になったら「切れた」と分かる（MemberApp/app/session-expiry.ts）。
    // ログインしていないときは外さない——切れた後に読み込み直した画面でも、理由を出せるように
    if (session.signedIn) {
        try {
            globalThis.sessionStorage.setItem(signedInMark, "1");
        } catch {
            // 使えなければ理由を出さないだけ
        }
    }

    const email = signedIn.querySelector<HTMLElement>("[data-auth-email]");
    if (email !== null) {
        // **文字として入れる**（アドレスは利用者が入れた値）
        email.textContent = session.signedIn ? session.email ?? "" : "";
    }
    for (const admin of root.querySelectorAll<HTMLElement>("[data-auth-admin]")) {
        admin.hidden = session.canOpenAdmin !== true;
    }
};

/** 行き先がこの製品の中か（<c>//evil.example</c> は別のサイト）。 */
const isLocalPath = (value: unknown): value is string =>
    typeof value === "string" && value.startsWith("/") && !value.startsWith("//");

/** ログアウトを送る。<b>送れなければ（通信が切れた）undefined。</b> */
const postLogout = async (token: string): Promise<Response | undefined> => {
    try {
        return await fetch("/api/account/logout", {
            method: "POST",
            headers: { ...jsonHeaders, RequestVerificationToken: token },
        });
    } catch {
        return undefined;
    }
};

/** 応答の行き先。<b>読めなければトップ</b>（切れたかどうかは別に確かめる）。 */
const locationOf = async (response: Response): Promise<string> => {
    try {
        const answer = await response.json() as { location?: unknown };
        return isLocalPath(answer.location) ? answer.location : "/";
    } catch {
        return "/";
    }
};

const signOut = async (root: Document, leave: Leave, tell: Tell): Promise<void> => {
    // 自分でログアウトしたときは「切れた」と言わない
    try {
        globalThis.sessionStorage.removeItem(signedInMark);
    } catch {
        // 使えなければ何もしない
    }
    // 次のページでログイン中に見せない
    clearAuthHint();
    const session = await readSession();
    if (session === undefined) {
        tell(signOutFailedMessage);
        return;
    }
    if (!session.signedIn) {
        // 既に切れている（別のタブでログアウトした）
        leave("/");
        return;
    }

    // ログイン中に戻す。印（signedInMark）も控えも書き直す
    const stay = (current: Session): void => {
        show(root, current);
        writeAuthHint(current);
        tell(signOutFailedMessage);
    };

    const response = await postLogout(session.token);
    if (response?.ok !== true) {
        stay(session);
        return;
    }
    const location = await locationOf(response);

    // **切れたことを聞き直す。**応答が 200 でも、ログインの Cookie が消えずに残れば、移った先でログイン中に戻って見える
    const after = await readSession();
    if (after?.signedIn === true) {
        stay(after);
        return;
    }
    leave(location);
};

export const setupHeaderSession = async (
    root: Document = document, leave: Leave = defaultLeave, tell: Tell = defaultTell): Promise<void> => {
    if (!("staticSite" in root.documentElement.dataset)) {
        return;
    }

    const refresh = async (): Promise<void> => {
        const session = await readSession();
        if (session !== undefined) {
            show(root, session);
            writeAuthHint(session);
        }
        // ここから先は読んだ結果（hidden）だけで出し分ける。読めなければ書き出したまま（ログイン前の入口）に戻す
        dropAuthHint(root);
    };

    root.querySelector("[data-auth-signout]")?.addEventListener("click", () => {
        void signOut(root, leave, tell);
    });
    root.addEventListener(sessionChanged, () => {
        void refresh();
    });

    await refresh();
};
